/**
 * 收藏卡片底部的操作条。
 *
 * M5 只真正启用"发送到此聊天"；共享 / 置顶 / 删除按钮继续占位，等后续 M。
 * "发送到此聊天" 有两层禁用条件：
 *   - 非 owner（kind='shared'）：共享来的内容不允许转发回聊天
 *   - 无 chat 上下文（未在飞书 webview 内或 SDK 没拿到 chat_id）
 *
 * 按钮触发后 1.5s 冷却，防止用户高频双击造成飞书限流。
 */

import { useState } from "react";

import { ApiError } from "@/api/client";
import { forwardFavorite } from "@/api/favorites";
import type { Item } from "@/api/items";
import { pinItem } from "@/api/items";
import { deleteFavorite } from "@/api/shares";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ShareDialog } from "@/components/ShareDialog";
import { toast } from "@/components/Toast";
import { chooseChat } from "@/feishu/jssdk";
import { useAppStore } from "@/store/auth";

const COOLDOWN_MS = 1500;

export function ActionBar({
  item,
  onDeleted,
  onPinChanged,
}: {
  item: Item;
  onDeleted?: () => void;
  onPinChanged?: () => void;
}) {
  const currentChat = useAppStore((s) => s.currentChat);
  const [busy, setBusy] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isOwn = item.kind === "own";
  const isPinned = !!item.pinned_at;
  const canForward = isOwn && !busy;
  const canShare = isOwn && !busy;
  const canDelete = isOwn && !busy;

  const onForward = async () => {
    if (!canForward) return;
    setBusy(true);
    try {
      let targetChatId = currentChat?.chat_id;

      // 无 currentChat 时（直接进入场景），弹出 chooseChat 让用户选择会话
      if (!targetChatId) {
        const picked = await chooseChat();
        if (!picked) {
          // 用户取消选择
          setBusy(false);
          return;
        }
        targetChatId = picked.chatId;
        // 更新 store，后续发送不再弹选择
        const chatType: "group" | "p2p" | "unknown" =
          picked.chatType === "group" || picked.chatType === "p2p"
            ? picked.chatType
            : "unknown";
        useAppStore.getState().setCurrentChat({
          chat_id: picked.chatId,
          chat_type: chatType,
        });
      }

      await forwardFavorite(item.favorite_id, {
        target_type: "chat",
        target_id: targetChatId,
        thread_id: currentChat?.thread_id,
      });
      // 转发成功，清除 sessionStorage 中的 pending chat（避免残留）
      try { sessionStorage.removeItem("feishu_fav_pending_chat"); } catch { /* */ }
      toast.success("已发送到当前聊天");
    } catch (e) {
      if (e instanceof ApiError) toast.error(friendlyMsg(e));
      else toast.error("发送失败");
    } finally {
      setTimeout(() => setBusy(false), COOLDOWN_MS);
    }
  };

  const onPin = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const rowId = item.kind === "shared" ? item.share_id! : item.favorite_id;
      await pinItem(item.kind, rowId, !isPinned);
      toast.success(isPinned ? "已取消置顶" : "已置顶");
      onPinChanged?.();
    } catch (e) {
      toast.error(e instanceof ApiError ? friendlyMsg(e) : "操作失败");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!canDelete) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    setBusy(true);
    try {
      await deleteFavorite(item.favorite_id);
      toast.success("已删除");
      onDeleted?.();
    } catch (e) {
      toast.error(e instanceof ApiError ? friendlyMsg(e) : "删除失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs">
      <button
        onClick={onForward}
        disabled={!canForward}
        title={!isOwn ? "共享给你的收藏不支持回推" : ""}
        className={`rounded px-2 py-1 transition ${
          canForward
            ? "bg-sky-50 text-sky-700 hover:bg-sky-100"
            : "cursor-not-allowed text-slate-400"
        }`}
      >
        {busy
          ? "处理中…"
          : currentChat?.chat_id
            ? "发送到此聊天"
            : "选择会话并发送"}
      </button>
      <button
        onClick={() => setShowShare(true)}
        disabled={!canShare}
        title={!isOwn ? "仅能共享自己的收藏" : ""}
        className={`rounded px-2 py-1 transition ${
          canShare
            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : "cursor-not-allowed text-slate-400"
        }`}
      >
        共享
      </button>
      <button
        onClick={onPin}
        disabled={busy}
        className={`rounded px-2 py-1 transition ${
          isPinned
            ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
            : "text-slate-600 hover:bg-slate-100"
        }`}
      >
        {isPinned ? "📌 取消置顶" : "📌 置顶"}
      </button>
      {isOwn ? (
        <button
          onClick={onDelete}
          disabled={!canDelete}
          className={`ml-auto rounded px-2 py-1 transition ${
            canDelete
              ? "text-rose-600 hover:bg-rose-50"
              : "cursor-not-allowed text-slate-400"
          }`}
        >
          删除
        </button>
      ) : null}

      {showShare ? (
        <ShareDialog
          favoriteId={item.favorite_id}
          onClose={() => setShowShare(false)}
        />
      ) : null}
      {showDeleteConfirm ? (
        <ConfirmDialog
          title="删除收藏"
          message="确认删除这条收藏？已共享给他人的通知卡片会标记为灰态。"
          confirmText="删除"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      ) : null}
    </div>
  );
}

function friendlyMsg(err: ApiError): string {
  switch (err.code) {
    case "FORWARD_FORBIDDEN":
      return "对该聊天无发送权限，或机器人未加入目标群";
    case "MESSAGE_GONE":
      return "原消息已不可访问";
    case "NO_PERMISSION":
      return "无权限";
    case "RATE_LIMITED":
      return "发送过于频繁，请稍后再试";
    case "NEED_REAUTH":
      return "登录已失效，正在重新授权";
    default:
      return err.message || "操作失败";
  }
}
